import { useState, useMemo } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useCustomer, formatApiErrorDetail } from "@/context/CustomerContext";
import { Input } from "@/components/ui/input";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";

const GENDERS = [
  { v: "male", l: "Male" },
  { v: "female", l: "Female" },
  { v: "other", l: "Other" },
  { v: "prefer_not_to_say", l: "Prefer not to say" },
];

function calcAge(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d)) return "";
  const t = new Date();
  let age = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) age--;
  return age >= 0 ? age : "";
}

export default function CustomerRegister() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/account";
  const { register } = useCustomer();
  const [form, setForm] = useState({
    full_name: "", mobile: "", password: "", dob: "", gender: "male",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const age = useMemo(() => calcAge(form.dob), [form.dob]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name || !form.mobile || !form.password || !form.dob) {
      setError("Please fill all the fields.");
      return;
    }
    setLoading(true);
    try {
      await register({
        full_name: form.full_name,
        mobile: form.mobile,
        password: form.password,
        dob: form.dob,
        gender: form.gender,
      });
      toast.success("Account created! You're all set.");
      navigate(next);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-card border border-stone-200 rounded-3xl p-7 shadow-sm">
        <h1 className="font-display font-extrabold text-3xl tracking-tight" data-testid="register-title">
          Create your account
        </h1>
        <p className="text-muted-foreground text-sm mt-1 mb-6">
          Book faster and track every celebration in one place.
        </p>
        <form onSubmit={submit} className="grid gap-3">
          <Input
            data-testid="register-name"
            placeholder="Full name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          />
          <Input
            data-testid="register-mobile"
            placeholder="Mobile number"
            inputMode="numeric"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
          />
          <Input
            data-testid="register-password"
            type="password"
            placeholder="Create a password (min 6 chars)"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground ml-1">Date of birth</label>
              <Input
                data-testid="register-dob"
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                value={form.dob}
                onChange={(e) => setForm({ ...form, dob: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground ml-1">Age</label>
              <Input data-testid="register-age" value={age === "" ? "" : `${age} yrs`} readOnly placeholder="Auto" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground ml-1">Gender</label>
            <select
              data-testid="register-gender"
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
              className="w-full h-10 rounded-md border border-input bg-card px-3 text-sm"
            >
              {GENDERS.map((g) => (
                <option key={g.v} value={g.v}>{g.l}</option>
              ))}
            </select>
          </div>
          {error && (
            <p data-testid="register-error" className="text-sm text-red-600 font-medium">
              {error}
            </p>
          )}
          <button
            data-testid="register-submit"
            type="submit"
            disabled={loading}
            className="mt-2 h-12 bg-primary text-white rounded-full font-semibold flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-primary/90 transition-colors"
          >
            <UserPlus className="h-5 w-5" /> {loading ? "Creating..." : "Create account"}
          </button>
        </form>
        <p className="text-sm text-center text-muted-foreground mt-5">
          Already have an account?{" "}
          <Link
            to={`/login${next !== "/account" ? `?next=${encodeURIComponent(next)}` : ""}`}
            data-testid="register-to-login"
            className="text-primary font-semibold"
          >
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
